import { normalizeUsGameKey, usGameSportForBoard, usPeriodForMarketType } from '../lib/polymarketUs/sports';
import { usFullGameTotalFamily } from '../lib/polymarketUs/totalTarget';
import { readUsMarketStatistics } from '../lib/polymarketUs/statistics';
import { usGameIdentity, usLineIdentity, type UsBrowserBoardQuote, type UsBrowserLineQuote,
  type UsGameIdentity, type UsLineIdentity } from './usBoardQuotes';

/** #1140: adapt gateway-owned public rows, without installing main's transport
 * or a second freshness clock. The receiving store must supply a live predicate
 * that expires on row/book replacement, withdrawal, source handoff and timeout.
 * The Node1 receiving store owns that predicate and publication lifecycle. */
export type UsGatewayBookEvidence =
  | { kind: 'row' }
  | { kind: 'book'; value: unknown; observedAtMs: number | null }
  | { kind: 'unavailable' };

export type UsGatewayQuote = UsBrowserBoardQuote | UsBrowserLineQuote;
export interface UsGatewayQuoteInput {
  row: unknown;
  /** `row` explicitly permits an owned top-of-book snapshot. A withdrawn or
   * malformed depth book must never silently fall back to that older row. */
  book: UsGatewayBookEvidence;
  currentAt: () => boolean;
}
const object = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const positiveTime = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v)
  && v > 0 && v <= 8_640_000_000_000_000;
const cents = (v: unknown): number | null => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100 ? v : null;
const usd = (v: number | null): string | null => v === null ? null : String(v / 100);
const complement = (v: number): number => Math.round((100 - v) * 1e8) / 1e8;
const emptyBook = (): UsBrowserBoardQuote['book'] => ({ observedAtMs: null,
  yesBidUsd: null, yesAskUsd: null, noBidUsd: null, noAskUsd: null });

function top(levels: unknown): number | null | undefined {
  if (!Array.isArray(levels)) return undefined;
  let result: number | null = null;
  for (const item of levels) {
    const level = object(item), price = cents(level?.price), size = level?.size;
    if (price === null || typeof size !== 'number' || !Number.isFinite(size) || size < 0) return undefined;
    if (size > 0 && (result === null || price > result)) result = price;
  }
  return result;
}

function quoteBook(row: Record<string, unknown>, evidence: UsGatewayBookEvidence):
{ book: UsBrowserBoardQuote['book']; valid: boolean } {
  if (evidence.kind === 'unavailable') return { book: emptyBook(), valid: false };
  if (evidence.kind === 'row') {
    const raw = [row.yesBid, row.yesAsk, row.noBid, row.noAsk];
    const observedAtMs = positiveTime(row.observedAtMs) ? row.observedAtMs
      : positiveTime(row.lastUpdateMs) ? row.lastUpdateMs : null;
    // Missing sides are unknown, not zero. A malformed present side invalidates
    // this observation; it must not remain clickable through another side.
    if (raw.some(value => value != null && cents(value) === null)) return { book: emptyBook(), valid: false };
    return { book: { observedAtMs, yesBidUsd: usd(cents(row.yesBid)), yesAskUsd: usd(cents(row.yesAsk)),
      noBidUsd: usd(cents(row.noBid)), noAskUsd: usd(cents(row.noAsk)) }, valid: observedAtMs !== null };
  }
  const value = object(evidence.value), yes = top(value?.yes), no = top(value?.no);
  if (!value || value.ticker != null && value.ticker !== row.ticker || yes === undefined || no === undefined
    || !positiveTime(evidence.observedAtMs)) return { book: emptyBook(), valid: false };
  return { book: { observedAtMs: evidence.observedAtMs, yesBidUsd: usd(yes), noBidUsd: usd(no),
    yesAskUsd: no === null ? null : usd(complement(no)), noAskUsd: yes === null ? null : usd(complement(yes)) }, valid: true };
}

/** Exact gateway identity and the existing shared display shape. Missing venue
 * and period are the receiver's documented wire allowances: source is still
 * mandatory, and sportsMarketType must independently name the full game. */
export function usGatewayQuote(input: UsGatewayQuoteInput): UsGatewayQuote | null {
  const row = object(input.row);
  if (!row || row.source !== 'polymarket_us' || row.venue !== undefined && row.venue !== 'polymarket_us'
    || row.marketType !== 'game' || row.status !== 'open' || row.dhAmbiguous === true
    || row.period !== undefined && row.period !== 'full'
    || typeof row.sportsMarketType !== 'string' || usPeriodForMarketType(row.sportsMarketType) !== 'full'
    || typeof row.pmusMarketSlug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(row.pmusMarketSlug)
    || row.ticker !== `PMUS-${row.pmusMarketSlug}`) return null;
  const base = typeof row.baseSport === 'string' ? row.baseSport : row.sport;
  const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
  const gameKey = sport ? normalizeUsGameKey(row.canonicalGameKeyV2, sport) : null;
  if (!sport || !gameKey || !gameKey.startsWith(sport.toLowerCase() + '|')) return null;
  const team = row.canonicalYesTeam, teams = gameKey.split('|').slice(3);
  const decoded = quoteBook(row, input.book);
  const shared = { marketSlug: row.pmusMarketSlug, gameKey, sport, book: decoded.book,
    statistics: readUsMarketStatistics(row.pmusStatistics, row.pmusMarketSlug),
    currentAt: () => decoded.valid && input.currentAt() };
  if (row.pmusMarketType === 'moneyline') {
    return typeof team === 'string' && teams.includes(team) ? { ...shared, yesTeam: team } : null;
  }
  const strike = row.floorStrike;
  if (typeof row.pmusMarketId !== 'string' || !/^[0-9]{1,32}$/.test(row.pmusMarketId)
    || typeof strike !== 'number' || !Number.isFinite(strike) || Math.abs(strike) > 1000
    || !/^-?[0-9]{1,4}(\.[0-9]{1,2})?$/.test(String(strike))) return null;
  const line = { ...shared, marketId: row.pmusMarketId, sportsMarketType: row.sportsMarketType,
    period: 'full' as const, line: String(strike) };
  if (row.pmusMarketType === 'spreads' && /full_game_spread$/.test(row.sportsMarketType)
    && typeof team === 'string' && teams.includes(team)) return { ...line, marketType: 'spreads', yesTeam: team };
  if (row.pmusMarketType === 'totals' && usFullGameTotalFamily(row.sportsMarketType, sport)
    && team == null && strike > 0) return { ...line, marketType: 'totals', yesTeam: null };
  return null;
}

export interface UsGatewayQuoteIndex {
  boardByGame: ReadonlyMap<string, UsBrowserBoardQuote>;
  linesByGame: ReadonlyMap<string, { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] }>;
  /** Exact-slug display marks only. Ambiguous slugs retain an unavailable
   * observation so a previously cached catalogue price cannot take over. */
  positionBySlug: ReadonlyMap<string, UsGatewayQuote>;
  games: readonly UsGameIdentity[];
  lines: readonly UsLineIdentity[];
}

/** One caller-owned snapshot, built only from its held US rows. The receiving
 * store controls when to rebuild; this function owns no subscriptions, timers,
 * retention or generation state. Quotes retain live invalidation callbacks. */
export function buildUsGatewayQuoteIndex(inputs: Iterable<UsGatewayQuoteInput>): UsGatewayQuoteIndex {
  const entries: { quote: UsGatewayQuote; row: Record<string, unknown> }[] = [];
  const positionBySlug = new Map<string, UsGatewayQuote>(), duplicateSlugs = new Set<string>();
  for (const input of inputs) {
    const quote = usGatewayQuote(input), row = object(input.row);
    if (!quote || !row) continue;
    entries.push({ quote, row });
    const old = positionBySlug.get(quote.marketSlug);
    if (old) duplicateSlugs.add(quote.marketSlug); else positionBySlug.set(quote.marketSlug, quote);
  }
  for (const slug of duplicateSlugs) {
    const old = positionBySlug.get(slug)!;
    positionBySlug.set(slug, { ...old, book: emptyBook(), statistics: null, currentAt: () => false });
  }
  const boardByGame = new Map<string, UsBrowserBoardQuote>(), duplicateGames = new Set<string>();
  const linesByGame = new Map<string, { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] }>();
  const games = new Map<string, UsGameIdentity>(), conflictingGames = new Set<string>(), lines: UsLineIdentity[] = [];
  for (const { quote, row } of entries) {
    if (duplicateSlugs.has(quote.marketSlug)) continue;
    if ('marketType' in quote) {
      const group = linesByGame.get(quote.gameKey) ?? { spreads: [], totals: [] };
      group[quote.marketType].push(quote); linesByGame.set(quote.gameKey, group);
    } else {
      if (boardByGame.has(quote.gameKey)) duplicateGames.add(quote.gameKey);
      else boardByGame.set(quote.gameKey, quote);
    }
    // Navigation needs oriented producer teams and start time, unlike display
    // joins to an already-known game. Never derive orientation from key order.
    const normalized = { ...row, venue: 'polymarket_us', period: 'full' };
    const line = 'marketType' in quote ? usLineIdentity(normalized) : null;
    const game = line?.game ?? usGameIdentity(normalized);
    if (!game) continue;
    const old = games.get(game.key);
    if (old && (old.awayCode !== game.awayCode || old.homeCode !== game.homeCode || old.startTimeMs !== game.startTimeMs)) {
      conflictingGames.add(game.key);
    } else games.set(game.key, game);
    if (line) lines.push(line);
  }
  for (const key of duplicateGames) boardByGame.delete(key);
  for (const key of conflictingGames) games.delete(key);
  return { boardByGame, linesByGame, positionBySlug, games: [...games.values()],
    lines: lines.filter(line => !conflictingGames.has(line.game.key)) };
}
